import { Box, TableContainer, Table as MuiTable, TableCell, TableHead, TableBody, TableRow } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import SortTableCell from '../../components/common/SortTableCell/SortTableCell';
import { useUserColumns } from '../../hooks/useUserColumns';
import { useUserSorts } from '../../hooks/useUserSorts';
import { columnBoxSx, droppableRowSx, tableContainerSx, tableWrapperContainerSx } from './ITableStyles';
import { listPageTableRowStyling } from '../../styles/sxStyling';
import ListBottomBar from '../../components/common/ListBottomBar/ListBottomBar';
import { ITableConfigurableModules } from '../../types/Itable';

export interface AvailableColumn<TData, TKey extends string = string> {
  key: TKey;
  label?: string;
  sortable?: boolean;
  render: (item: TData) => React.ReactNode;
}
export type ColumnSelectorOption<TKey extends string = string> = Pick<AvailableColumn<any, TKey>, 'key' | 'label'>;

export interface DynamicTableProps<TData, TKey extends string = string> {
  availableColumns: AvailableColumn<TData, TKey>[];
  data: TData[];
  module: ITableConfigurableModules;
  page: number;
  count: number;
  onPageChange?: (e: React.ChangeEvent<unknown>, value: number) => void;
}

const ITable = <TData, TKey extends string = string>({ availableColumns, data, module, page, count, onPageChange }: DynamicTableProps<TData, TKey>) => {
  const { userColumns, onUpdateColumns } = useUserColumns(module);
  const { userSorts, onSort } = useUserSorts(module);

  // keep track of the hovered row index
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const activeColumns = useMemo(() => {
    return userColumns.map((col) => availableColumns.find((c) => c.key === col.key)).filter((col): col is AvailableColumn<TData, TKey> => !!col);
  }, [userColumns, availableColumns]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = [...userColumns];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onUpdateColumns(reordered);
  };

  return (
    <TableContainer sx={tableContainerSx}>
      <Box sx={tableWrapperContainerSx}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="columns" direction="horizontal" type="column">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps} sx={droppableRowSx}>
                {activeColumns.map((col, colIndex) => (
                  <Draggable draggableId={col.key} index={colIndex} key={col.key}>
                    {(drag, snapshot) => (
                      <Box ref={drag.innerRef} {...drag.draggableProps} sx={columnBoxSx}>
                        <MuiTable>
                          <TableHead>
                            <TableRow {...drag.dragHandleProps}>
                              <SortTableCell column={col} userSorts={userSorts} onSort={onSort} isDragging={snapshot.isDragging}>
                                {col.label}
                              </SortTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.map((item, rowIndex) => (
                              <TableRow
                                key={rowIndex}
                                onMouseEnter={() => setHoveredRow(rowIndex)}
                                onMouseLeave={() => setHoveredRow(null)}
                                sx={{
                                  ...listPageTableRowStyling,
                                  height: 48,
                                  ...(hoveredRow === rowIndex && {
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                  }),
                                }}
                              >
                                <TableCell sx={{ py: 0, pl: 2 }}>{col.render(item)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </MuiTable>
                      </Box>
                    )}
                  </Draggable>
                ))}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <ListBottomBar page={page} count={count} onChange={onPageChange} selectedKeys={userColumns.map((c) => c.key)} />
    </TableContainer>
  );
};

export default React.memo(ITable) as typeof ITable;
